/* Two scores, differenced. Pure — no React, no fetch.
 *
 * **One computation serves both "why did it change" and "compare runs", deliberately.** They are
 * the same arithmetic read at two altitudes: the first wants the top three contributors in a
 * sentence, the second wants every dimension and every rule in a table. Computing them separately
 * is how a screen comes to say consistency was the cause while the table beside it ranks validity
 * first — the two-vocabularies failure, on the one number a review turns on.
 *
 * Everything here comes off two `Score` payloads and nothing else. No threshold, no heuristic, no
 * generated prose: `points_lost` is on the wire, `sum(points_lost)` is exactly `100 - value` by the
 * contract's own guarantee, and the ranking below is that arithmetic sorted.
 */

import type { Contribution, Dimension, Score } from '../api/scoring';

/** One dimension's movement between two runs. */
export interface DimensionMove {
  readonly dimension: Dimension;
  readonly now: number | null;
  readonly before: number | null;
  /** `null` when either side held no evidence — never a zero, which would read as "did not move". */
  readonly delta: number | null;
  /** The newer run's weight for this dimension. */
  readonly weight: number;
  /** `delta * weight`: the dimension's share of the *overall* movement, and the ranking key. A
   * ten-point fall in a dimension weighted 0.02 moves the total by 0.2 and is not the story. */
  readonly weighted: number | null;
  /** Points the newer run lost in this dimension. */
  readonly pointsLost: number;
  /** Rules attributed to it that found at least one violation in the newer run. */
  readonly failedChecks: number;
  readonly rulesCounted: number;
  /** Violations across those rules in the newer run. */
  readonly affectedRecords: number;
}

export type RuleChange = 'added' | 'removed' | 'worsened' | 'improved' | 'unchanged';

/** One rule's movement between two runs, matched on the rule's *name*.
 *
 * Matched on name rather than on `rule_version_id` because a re-versioned rule keeps its name and
 * changes its id: matching on the id would report every version bump as one rule removed and
 * another added, and hide the pass-rate change that is the actual finding. `labelChanged` is what
 * carries the version bump instead, which is §14's "changed evaluation rules".
 */
export interface RuleMove {
  readonly ruleName: string;
  readonly ruleLabel: string;
  readonly previousLabel: string | null;
  readonly labelChanged: boolean;
  readonly dimension: Dimension;
  readonly severity: Contribution['severity'];
  readonly passNow: number | null;
  readonly passBefore: number | null;
  readonly passDelta: number | null;
  readonly violationsNow: number;
  readonly violationsBefore: number | null;
  readonly rowsEvaluated: number;
  readonly pointsLost: number;
  /** Violations the newer run found that the older did not. Floored at zero: a fall in the count is
   * a resolution, and reporting it as a negative "new" figure would put a minus sign in a column
   * whose whole meaning is "how much more broke". */
  readonly newFailures: number;
  readonly resolvedFailures: number;
  readonly change: RuleChange;
}

export interface RunDiff {
  readonly overallNow: number | null;
  readonly overallBefore: number | null;
  /** `null` when either side is unscored **or** when the two runs used different score models. */
  readonly overallDelta: number | null;
  /**
   * False when the two runs were scored under different models. The dimension and rule rows are
   * still shown — a rule's pass rate is a measurement and does not depend on the weighting — but
   * the *total* is two different definitions and a delta between them would attribute a
   * reweighting to the data.
   */
  readonly comparable: boolean;
  /**
   * True when both runs carry the same execution plan hash — provably the same rule versions
   * against the same columns of the same dataset version. This is the first fact a diagnosis needs:
   * with it, any difference below is a change in the *data*; without it, part of the difference is
   * configuration.
   */
  readonly samePlan: boolean;
  readonly planNow: string;
  readonly planBefore: string;
  readonly modelNow: number;
  readonly modelBefore: number;
  readonly datasetVersionChanged: boolean;
  readonly coverageNow: number | null;
  readonly coverageBefore: number | null;
  /** Ranked worst-first by `weighted`; dimensions with no movement fall to the end. */
  readonly dimensions: readonly DimensionMove[];
  /** Ranked by how much each rule moved, then by what it cost. */
  readonly rules: readonly RuleMove[];
  readonly newFailures: number;
  readonly resolvedFailures: number;
  readonly rulesAdded: number;
  readonly rulesRemoved: number;
  readonly rulesReVersioned: number;
}

/* Only reachable if a name came out of neither map, which the set above makes impossible. Named
 * rather than asserted, so the impossible case has a value instead of a non-null assertion that a
 * future edit could make reachable without noticing. */
const FALLBACK_DIMENSION: Dimension = 'completeness';

function byName(
  contributions: readonly Contribution[],
): ReadonlyMap<string, Contribution> {
  return new Map(contributions.map((entry) => [entry.rule_name, entry]));
}

/** A movement below this is rounding, not a finding. Mirrors `MOVEMENT_FLOOR` in `presentation.ts`
 * for the same reason: a dashboard full of arrows that mean nothing is worse than no arrows. */
const MOVEMENT_FLOOR = 0.1;

/**
 * `newer` against `older`. Both are full `Score` payloads with their decompositions.
 *
 * The caller is responsible for handing these over in the right order; the diff does not sort them
 * by timestamp, because "which of these two runs is the newer" is a question the caller already
 * answered when it chose them and a second answer here could disagree with the labels on screen.
 */
export function compareScores(newer: Score, older: Score): RunDiff {
  const comparable = newer.score_model_version === older.score_model_version;
  const beforeDims = new Map(older.dimensions.map((entry) => [entry.dimension, entry]));

  const dimensions: DimensionMove[] = newer.dimensions.map((entry) => {
    const before = beforeDims.get(entry.dimension)?.value ?? null;
    const delta = entry.value !== null && before !== null ? entry.value - before : null;
    const forDimension = newer.contributions.filter(
      (c) => c.dimension === entry.dimension,
    );
    return {
      dimension: entry.dimension,
      now: entry.value,
      before,
      delta,
      weight: entry.weight,
      weighted: delta === null ? null : delta * entry.weight,
      pointsLost: forDimension.reduce((sum, c) => sum + c.points_lost, 0),
      failedChecks: forDimension.filter((c) => c.violation_count > 0).length,
      rulesCounted: entry.rules_counted,
      affectedRecords: forDimension.reduce((sum, c) => sum + c.violation_count, 0),
    };
  });

  /* Worst weighted movement first; an unmoved or unmeasurable dimension sorts after everything that
     moved, in the payload's own order. Sorting the nulls in among the numbers would put "we cannot
     say" between two findings, which is where a reader stops reading. */
  dimensions.sort((left, right) => {
    if (left.weighted === null && right.weighted === null) return 0;
    if (left.weighted === null) return 1;
    if (right.weighted === null) return -1;
    return left.weighted - right.weighted;
  });

  const beforeRules = byName(older.contributions);
  const nowRules = byName(newer.contributions);
  const names = new Set([...nowRules.keys(), ...beforeRules.keys()]);

  const rules: RuleMove[] = [...names].map((name) => {
    const now = nowRules.get(name);
    const before = beforeRules.get(name);
    /* One of the two is always defined — the name came from one of the maps. The fallback keeps
       the type honest without a non-null assertion. */
    const anchor = now ?? before;
    const passNow = now?.pass_ratio ?? null;
    const passBefore = before?.pass_ratio ?? null;
    const violationsNow = now?.violation_count ?? 0;
    const violationsBefore = before?.violation_count ?? null;
    const passDelta =
      passNow !== null && passBefore !== null ? passNow - passBefore : null;

    const change: RuleChange =
      now === undefined
        ? 'removed'
        : before === undefined
          ? 'added'
          : passDelta === null || Math.abs(passDelta) < 0.0005
            ? 'unchanged'
            : passDelta < 0
              ? 'worsened'
              : 'improved';

    return {
      ruleName: name,
      ruleLabel: anchor?.rule_label ?? '',
      previousLabel: before?.rule_label ?? null,
      labelChanged:
        now !== undefined && before !== undefined && now.rule_label !== before.rule_label,
      dimension: anchor?.dimension ?? FALLBACK_DIMENSION,
      severity: anchor?.severity ?? 'info',
      passNow,
      passBefore,
      passDelta,
      violationsNow,
      violationsBefore,
      rowsEvaluated: now?.rows_evaluated ?? before?.rows_evaluated ?? 0,
      pointsLost: now?.points_lost ?? 0,
      newFailures:
        violationsBefore === null
          ? violationsNow
          : Math.max(0, violationsNow - violationsBefore),
      resolvedFailures:
        violationsBefore === null ? 0 : Math.max(0, violationsBefore - violationsNow),
      change,
    };
  });

  /* A rule that moved outranks one that did not, whatever either cost — the subject of this table
     is the difference between two runs, and a stable rule losing eight points every time is not
     news. Within each group, by points lost. */
  rules.sort((left, right) => {
    const moved = (entry: RuleMove): number => (entry.change === 'unchanged' ? 1 : 0);
    return (
      moved(left) - moved(right) ||
      Math.abs(right.passDelta ?? 1) - Math.abs(left.passDelta ?? 1) ||
      right.pointsLost - left.pointsLost ||
      left.ruleName.localeCompare(right.ruleName)
    );
  });

  const overallDelta =
    comparable && newer.value !== null && older.value !== null
      ? newer.value - older.value
      : null;

  return {
    overallNow: newer.value,
    overallBefore: older.value,
    overallDelta:
      overallDelta !== null && Math.abs(overallDelta) < MOVEMENT_FLOOR ? 0 : overallDelta,
    comparable,
    samePlan: newer.plan_hash === older.plan_hash,
    planNow: newer.plan_hash,
    planBefore: older.plan_hash,
    modelNow: newer.score_model_version,
    modelBefore: older.score_model_version,
    datasetVersionChanged: newer.dataset_version_id !== older.dataset_version_id,
    coverageNow: newer.coverage.column_ratio,
    coverageBefore: older.coverage.column_ratio,
    dimensions,
    rules,
    newFailures: rules.reduce((sum, rule) => sum + rule.newFailures, 0),
    resolvedFailures: rules.reduce((sum, rule) => sum + rule.resolvedFailures, 0),
    rulesAdded: rules.filter((rule) => rule.change === 'added').length,
    rulesRemoved: rules.filter((rule) => rule.change === 'removed').length,
    rulesReVersioned: rules.filter((rule) => rule.labelChanged).length,
  };
}

/**
 * The contributors a "why did it change" section names, worst first.
 *
 * Only dimensions that actually moved, because a section headed "primary contributors" that lists a
 * dimension which did not move is one a reader stops trusting. Bounded to `limit`, because ranking
 * seven dimensions and calling all seven primary says nothing.
 */
export function primaryContributors(diff: RunDiff, limit = 3): readonly DimensionMove[] {
  return diff.dimensions
    .filter(
      (move) => move.weighted !== null && Math.abs(move.weighted) >= MOVEMENT_FLOOR / 10,
    )
    .slice(0, limit);
}

/** The rules inside one dimension that moved, worst first — the third rung of the drill-down. */
export function movedRulesIn(diff: RunDiff, dimension: Dimension): readonly RuleMove[] {
  return diff.rules.filter(
    (rule) => rule.dimension === dimension && rule.change !== 'unchanged',
  );
}
